import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { fetchProducts } from "../../lib/api";
import { DIWALI_INFO, waLink } from "../../constants/data";
import { Sparkle, Star, Flower } from "../doodles/Doodles";

/**
 * DiwaliHampers — Diwali seasonal section on the home page.
 * - Renders a "Diwali is on 8 November 2026" reminder
 * - Displays 5 hamper tiles (fetched from /api/products?category=diwali) as a
 *   horizontally swipeable + auto-rotating carousel
 * - Each tile is clickable → /product/<slug>
 * - Admin manages the 5 products via the existing Admin → Products tab
 *   (no separate admin UI needed)
 */
export default function DiwaliHampers() {
  const [hampers, setHampers] = useState([]);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const scrollerRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    fetchProducts({ category: "diwali", sort: "position" })
      .then((data) => setHampers((data || []).slice(0, 5)))
      .catch(() => setHampers([]));
  }, []);

  // Auto-rotate every 4.5s (pauses on hover / touch)
  useEffect(() => {
    if (hampers.length <= 1 || paused) return;
    const t = setInterval(() => {
      setActive((i) => (i + 1) % hampers.length);
    }, 4500);
    return () => clearInterval(t);
  }, [hampers.length, paused]);

  // Smooth-scroll the active card into view (mobile & desktop)
  useEffect(() => {
    const el = cardRefs.current[active];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }
  }, [active]);

  const go = (dir) => {
    if (!hampers.length) return;
    setActive((i) => (i + dir + hampers.length) % hampers.length);
  };

  return (
    <section
      className="section-y relative overflow-hidden bg-gradient-to-br from-[#FFF6E8] via-[#FFEAD4] to-[#FFDAB2]"
      data-testid="diwali-hampers-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Decorative doodles */}
      <Flower color="#E08036" className="absolute -top-4 -left-4 w-24 rotate-12 opacity-70 pointer-events-none"/>
      <Flower color="#FFA94D" className="absolute -bottom-4 -right-4 w-24 -rotate-12 opacity-70 pointer-events-none"/>
      <Sparkle color="#FFD93D" className="absolute top-8 right-12 w-6 animate-twinkle pointer-events-none"/>
      <Sparkle color="#FF6B9D" className="absolute bottom-12 left-8 w-5 animate-twinkle pointer-events-none" style={{ animationDelay: "0.8s" }}/>
      <Star color="#FFD93D" className="absolute top-1/2 right-1/4 w-6 animate-spin-slow pointer-events-none hidden sm:block"/>

      <div className="container-x relative">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-8">
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border-2 border-[#7A1F2B] font-hand text-[11px] uppercase tracking-widest text-[#7A1F2B] -rotate-2 rounded-full"
            style={{ boxShadow: "3px 3px 0 #7A1F2B" }}
            data-testid="diwali-reminder-stamp"
          >
            <Sparkles className="w-3 h-3"/> {DIWALI_INFO.greeting} 🪔
          </span>
          <h2 className="mt-3 font-doodle text-3xl sm:text-4xl lg:text-5xl text-[#7A1F2B] leading-tight">
            Diwali <span className="text-[#E08036]">Hampers</span>
          </h2>
          <p className="mt-2 font-hand text-sm sm:text-base text-[#5C5555] italic">
            Handcrafted festive hampers — sweets, diyas, dry fruits &amp; heartfelt keepsakes.
          </p>
        </div>

        {/* Carousel */}
        {hampers.length === 0 ? (
          <div
            className="max-w-md mx-auto p-6 bg-white/70 border-2 border-dashed border-[#7A1F2B]/40 rounded-2xl text-center"
            data-testid="diwali-empty-state"
          >
            <p className="font-hand text-sm text-[#5C5555]">
              Diwali hampers arriving soon. Admin can add them under <b>Products → category "diwali"</b>.
            </p>
          </div>
        ) : (
          <div className="relative">
            {/* Prev/Next (desktop) */}
            {hampers.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => go(-1)}
                  data-testid="diwali-prev"
                  aria-label="previous hamper"
                  className="hidden sm:flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-white border-2 border-[#2D2424] rounded-full items-center justify-center hover:bg-[#FFE7F1]"
                  style={{ boxShadow: "3px 3px 0 #2D2424" }}
                >
                  <ChevronLeft className="w-5 h-5"/>
                </button>
                <button
                  type="button"
                  onClick={() => go(1)}
                  data-testid="diwali-next"
                  aria-label="next hamper"
                  className="hidden sm:flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-white border-2 border-[#2D2424] rounded-full items-center justify-center hover:bg-[#FFE7F1]"
                  style={{ boxShadow: "3px 3px 0 #2D2424" }}
                >
                  <ChevronRight className="w-5 h-5"/>
                </button>
              </>
            )}

            {/* Scroller — swipe-friendly on mobile, snap to card */}
            <div
              ref={scrollerRef}
              onTouchStart={() => setPaused(true)}
              onTouchEnd={() => setTimeout(() => setPaused(false), 2000)}
              data-testid="diwali-scroller"
              className="flex gap-4 sm:gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-3 -mx-4 px-4 sm:mx-0 sm:px-0"
              style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
            >
              {hampers.map((h, i) => (
                <div
                  key={h.id}
                  ref={(el) => (cardRefs.current[i] = el)}
                  data-testid={`diwali-hamper-${i}`}
                  className={`shrink-0 snap-center w-[78%] sm:w-[42%] lg:w-[30%] group ${i === active ? "scale-[1.02]" : "scale-100"} transition-transform`}
                >
                  <div
                    className="doodle-card overflow-hidden bg-white rotate-[-1deg] group-hover:rotate-0 transition-transform"
                    style={{ boxShadow: "6px 6px 0 #7A1F2B" }}
                  >
                    <div className="relative aspect-[4/5] overflow-hidden bg-[#FFE7F1]">
                      <img
                        src={h.images?.[0] || "https://images.unsplash.com/photo-1699801676350-4182399f7cdd?w=900&q=80"}
                        alt={h.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <span
                        data-testid={`diwali-coming-soon-${i}`}
                        className="absolute top-3 left-3 inline-flex items-center gap-1 px-3 py-1 bg-[#7A1F2B] text-white font-hand text-[11px] uppercase tracking-widest -rotate-3 rounded-full"
                        style={{ boxShadow: "2px 2px 0 #FFD93D" }}
                      >
                        <Sparkles className="w-3 h-3"/> Coming Soon
                      </span>
                    </div>
                    <div className="p-4">
                      <p className="font-doodle text-xl text-[#7A1F2B] leading-tight line-clamp-2">{h.name}</p>
                      <div className="mt-2 flex items-center justify-between">
                        <p className="font-serif text-lg text-[#2D2424]">₹{(h.price || 0).toLocaleString("en-IN")}</p>
                        <span className="doodle-pill bg-[#FFF7DC] text-[#7A1F2B] text-[10px]">Launching 8 Nov</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Dots */}
            {hampers.length > 1 && (
              <div className="mt-6 flex justify-center gap-2" data-testid="diwali-dots">
                {hampers.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActive(i)}
                    data-testid={`diwali-dot-${i}`}
                    aria-label={`go to hamper ${i + 1}`}
                    className={`h-2 rounded-full transition-all ${i === active ? "w-8 bg-[#7A1F2B]" : "w-2 bg-[#7A1F2B]/30 hover:bg-[#7A1F2B]/60"}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-8 text-center">
          <a
            href={waLink(`Hi Naathi Charami! 🪔 Please notify me when the Diwali Hampers collection launches on ${DIWALI_INFO.date}.`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="diwali-notify-whatsapp"
            className="btn-primary"
          >
            <Sparkles className="w-4 h-4"/> Notify me on WhatsApp
          </a>
        </div>
      </div>

      {/* Hide scrollbar */}
      <style>{`
        [data-testid='diwali-scroller']::-webkit-scrollbar { display: none; }
      `}</style>
    </section>
  );
}
